import {DateTime} from "luxon";
import {container} from "tsyringe";
import {DataTransferObject} from "../domain/dtos/data-transfer-object.interface";
import {Entity} from "../domain/entities/entity";
import {EntityState} from "../persistence/entity-state.enum";
import {EntityManager} from "../persistence/entity.manager";
import {TransformerInterface} from "./transformerInterface";

export abstract class AbstractDtoToEntityTransformer<S extends DataTransferObject, T extends Entity> implements TransformerInterface<T, S> {

    /**
     * In-flight transformations cache to prevent duplicate requests.
     * When multiple parallel transforms request the same UID, only one
     * actually runs toEntity() - others wait for the same Promise.
     */
    private static _inFlightTransforms: Map<string, Promise<Entity>> = new Map();

    protected get entityManager(): EntityManager {
        return container.resolve(EntityManager);
    }

    public async transform(inputObject: S): Promise<T> {
        const cacheKey = inputObject.uid;

        // Already loaded in EntityManager - return immediately
        if (this.entityManager.isLoaded(cacheKey)) {
            return this.entityManager.getLoadedEntity(cacheKey) as T;
        }

        // Check if transformation is already in progress for this UID
        if (AbstractDtoToEntityTransformer._inFlightTransforms.has(cacheKey)) {
            await AbstractDtoToEntityTransformer._inFlightTransforms.get(cacheKey);
            return this.entityManager.getLoadedEntity(cacheKey) as T;
        }

        // Start new transformation and track it
        const transformPromise = this._doTransform(inputObject, cacheKey);
        AbstractDtoToEntityTransformer._inFlightTransforms.set(cacheKey, transformPromise);

        try {
            await transformPromise;
        } finally {
            // Clean up in-flight tracking after completion
            AbstractDtoToEntityTransformer._inFlightTransforms.delete(cacheKey);
        }

        return this.entityManager.getLoadedEntity(cacheKey) as T;
    }

    /**
     * Performs the actual transformation work.
     */
    private async _doTransform(inputObject: S, cacheKey: string): Promise<T> {
        const entity = await this.toEntity(inputObject);

        // Handle null return from toEntity (e.g., soft-deleted references that couldn't be loaded)
        if (!entity) {
            console.error(`Failed to transform entity with uid ${inputObject.uid} - toEntity returned null`);
            return this.entityManager.getLoadedEntity(cacheKey) as T;
        }

        Object.assign(entity, {
            uid: inputObject.uid,
            sorting: inputObject.sorting,
            createdAt: DateTime.fromSQL(inputObject.createdAt ?? `1970-01-01 00:00:00.000`)
        });

        if (`color` in inputObject) {
            entity.color = inputObject.color;
            // Always, also as null (feature plan 0074): a key the setter never ran for has no starting value, and
            // the first `textColor = …` in a dialog would become that starting value instead of a change.
            entity.textColor = (inputObject as {textColor?: string | null}).textColor ?? null;
        }

        this.entityManager.setLoadedEntity(entity, EntityState.PERSISTED);
        return entity;
    }

    protected abstract toEntity(dto: S): Promise<T>;
}